import React from 'react';

import { boolLabel } from './utils';
import { Button } from '../../components';

import './styles.scoped.css';

/**
 * CP-857: react-table column definitions for the code table grids.
 *
 * Lives outside the screen component (same reason SiteList and Table Contents keep theirs
 * in a module) so the Cell renderers are not redefined on every render, which would remount
 * every cell as the user types in the filter.
 */

const ValueCell = ({ field, row }) => {
  const value = row.original[field.key];

  if (field.type === 'boolean') {
    return <span className={boolLabel(value) === 'Yes' ? 'flag-yes' : 'flag-no'}>{boolLabel(value)}</span>;
  }
  // A blank branch or cost center is meaningful here -- a DMS aggregate or a deliberately
  // unmapped worklist -- so render it as something visible rather than an empty cell.
  if (field.type === 'number' && String(value ?? '').trim() === '') {
    return <span className="value-blank">—</span>;
  }
  return <span>{value}</span>;
};

/**
 * The key cell flags a value TenStreet has never sent. That mapping or classification
 * matches nothing and would otherwise fail silently, which is the fault these screens
 * exist to catch.
 */
const KeyCell = ({ field, row, isUnseen }) => (
  <span>
    {row.original[field.key]}
    {isUnseen(row.original[field.key]) && <span className="unseen-tag">not in TenStreet</span>}
  </span>
);

const RowActionsCell = ({ row, onEdit, onHistory, keyField }) => (
  <span className="row-actions">
    <Button variant="text" text="Edit" onPress={() => onEdit(row.original)} />
    <Button variant="text" text="History" onPress={() => onHistory(row.original[keyField])} />
  </span>
);

export const buildColumns = ({ config, onEdit, onHistory, isUnseen }) => [
  ...config.fields.map(field => ({
    // `Header` stays a plain string: TableFilter keys its dropdown by it and the screen
    // matches the chosen column by it, so a node here would break both.
    Header: field.label,
    hint: field.hint,
    accessor: field.key,
    Cell: ({ row }) =>
      field.isKey ? <KeyCell field={field} row={row} isUnseen={isUnseen} /> : <ValueCell field={field} row={row} />,
  })),
  {
    Header: '',
    id: 'rowActions',
    disableSortBy: true,
    disableFilters: true,
    Cell: ({ row }) => <RowActionsCell row={row} onEdit={onEdit} onHistory={onHistory} keyField={config.keyField} />,
  },
];
